import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Grid, Card, CardContent, Button, TextField, Table, 
    TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
    Chip, Switch, FormControl, InputLabel, Select, MenuItem, Dialog, 
    DialogTitle, DialogContent, DialogActions, FormControlLabel, CircularProgress, 
    Alert, Checkbox, FormGroup, Divider
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { useApp } from '../context/AppContext';
import API_BASE_URL from '../config/api';

const TENANT_DDL_SQL_SCRIPT = `-- ================================================================
-- DINEXA POS - LATEST TENANT DATABASE DDL SCHEMA & SEED
-- Compatible with Hostinger MySQL & MariaDB
-- ================================================================

SET FOREIGN_KEY_CHECKS = 0;

-- 1. BRANCHES TABLE
CREATE TABLE IF NOT EXISTS \`branches\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`name\` VARCHAR(255) NOT NULL,
    \`address\` TEXT NULL,
    \`phone\` VARCHAR(20) NULL,
    \`upi_id\` VARCHAR(255) NULL,
    \`upi_merchant_name\` VARCHAR(255) NULL,
    \`receipt_template\` VARCHAR(50) DEFAULT 'format2',
    \`print_feed_lines\` INT DEFAULT 3,
    \`kot_enabled\` TINYINT(1) DEFAULT 1,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 2. USERS TABLE
CREATE TABLE IF NOT EXISTS \`users\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`name\` VARCHAR(255) NOT NULL,
    \`email\` VARCHAR(255) UNIQUE NOT NULL,
    \`password\` VARCHAR(255) NOT NULL,
    \`role\` ENUM('owner', 'manager', 'captain', 'billing_staff', 'kitchen_staff') NOT NULL DEFAULT 'owner',
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 3. SECTIONS TABLE
CREATE TABLE IF NOT EXISTS \`sections\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`name\` VARCHAR(255) NOT NULL,
    \`display_order\` INT DEFAULT 0,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 4. DINING TABLES TABLE
CREATE TABLE IF NOT EXISTS \`dining_tables\` (
    \`id\` VARCHAR(50) PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`section_id\` INT NULL,
    \`number\` VARCHAR(50) NOT NULL,
    \`display_order\` INT DEFAULT 0,
    \`status\` ENUM('vacant', 'occupied', 'billed', 'dirty', 'reserved', 'split') DEFAULT 'vacant',
    \`active_order_id\` VARCHAR(50) NULL,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE,
    FOREIGN KEY (\`section_id\`) REFERENCES \`sections\`(\`id\`) ON DELETE SET NULL
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 5. CATEGORIES TABLE
CREATE TABLE IF NOT EXISTS \`categories\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`branch_id\` INT NULL DEFAULT 1,
    \`name\` VARCHAR(255) NOT NULL,
    \`name_mr\` VARCHAR(255) NULL,
    \`name_hi\` VARCHAR(255) NULL,
    \`display_order\` INT DEFAULT 0,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 6. MENU ITEMS TABLE
CREATE TABLE IF NOT EXISTS \`menu_items\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`name\` VARCHAR(255) NOT NULL,
    \`name_mr\` VARCHAR(255) NULL,
    \`name_hi\` VARCHAR(255) NULL,
    \`category_id\` INT NOT NULL,
    \`price\` DECIMAL(10, 2) NOT NULL,
    \`veg\` TINYINT(1) DEFAULT 1,
    \`is_veg\` TINYINT(1) DEFAULT 1,
    \`is_available\` TINYINT(1) DEFAULT 1,
    \`image_url\` VARCHAR(1000) NULL,
    \`display_order\` INT DEFAULT 0,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE,
    FOREIGN KEY (\`category_id\`) REFERENCES \`categories\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 7. ORDERS TABLE
CREATE TABLE IF NOT EXISTS \`orders\` (
    \`id\` VARCHAR(50) PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`table_id\` VARCHAR(50) NULL,
    \`type\` VARCHAR(50) NOT NULL DEFAULT 'Takeaway',
    \`order_source\` VARCHAR(50) DEFAULT 'takeaway',
    \`token_number\` INT NULL DEFAULT NULL,
    \`aggregator_order_id\` VARCHAR(100) NULL,
    \`status\` ENUM('pending', 'cooking', 'served', 'cancelled') DEFAULT 'cooking',
    \`waiter_name\` VARCHAR(100) NULL,
    \`subtotal\` DECIMAL(10, 2) NOT NULL DEFAULT 0.00,
    \`tax\` DECIMAL(10, 2) NOT NULL DEFAULT 0.00,
    \`service_charge\` DECIMAL(10, 2) DEFAULT 0.00,
    \`discount\` DECIMAL(10, 2) DEFAULT 0.00,
    \`discount_reason\` VARCHAR(500) NULL,
    \`total\` DECIMAL(10, 2) NOT NULL DEFAULT 0.00,
    \`payment_method\` VARCHAR(100) NULL DEFAULT 'cash',
    \`customer_phone\` VARCHAR(20) NULL,
    \`customer_consent\` TINYINT(1) DEFAULT 0,
    \`cancel_reason\` VARCHAR(500) NULL,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 8. ORDER ITEMS TABLE
CREATE TABLE IF NOT EXISTS \`order_items\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`order_id\` VARCHAR(50) NOT NULL,
    \`menu_item_id\` INT NOT NULL,
    \`quantity\` INT NOT NULL DEFAULT 1,
    \`price\` DECIMAL(10, 2) NOT NULL,
    \`remarks\` TEXT NULL,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`order_id\`) REFERENCES \`orders\`(\`id\`) ON DELETE CASCADE,
    FOREIGN KEY (\`menu_item_id\`) REFERENCES \`menu_items\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 9. INVENTORY TABLE
CREATE TABLE IF NOT EXISTS \`inventory\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`name\` VARCHAR(255) NOT NULL,
    \`qty\` DECIMAL(10, 2) NOT NULL DEFAULT 0.00,
    \`unit\` VARCHAR(20) NOT NULL DEFAULT 'kg',
    \`threshold\` DECIMAL(10, 2) NOT NULL DEFAULT 0.00,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 10. RECIPES TABLE
CREATE TABLE IF NOT EXISTS \`recipes\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`menu_item_id\` INT NOT NULL,
    \`ingredient_id\` INT NOT NULL,
    \`weight\` DECIMAL(10, 3) NOT NULL,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`menu_item_id\`) REFERENCES \`menu_items\`(\`id\`) ON DELETE CASCADE,
    FOREIGN KEY (\`ingredient_id\`) REFERENCES \`inventory\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 11. LEDGERS (EXPENSES / INWARD / OUTWARD)
CREATE TABLE IF NOT EXISTS \`ledgers\` (
    \`id\` INT AUTO_INCREMENT PRIMARY KEY,
    \`branch_id\` INT NOT NULL DEFAULT 1,
    \`type\` ENUM('inward', 'outward', 'expense') NOT NULL,
    \`description\` TEXT NULL,
    \`amount\` DECIMAL(10, 2) NOT NULL,
    \`date\` DATE NOT NULL,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
    FOREIGN KEY (\`branch_id\`) REFERENCES \`branches\`(\`id\`) ON DELETE CASCADE
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- 12. ROLE PERMISSIONS TABLE
CREATE TABLE IF NOT EXISTS \`role_permissions\` (
    \`role\` VARCHAR(50) PRIMARY KEY,
    \`allowed_menus\` JSON NULL,
    \`locked_actions\` JSON NULL,
    \`created_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;

-- ================================================================
-- INITIAL SEED DATA (DEFAULT STARTER RECORDS)
-- ================================================================

-- Default Branch 1
INSERT INTO \`branches\` (\`id\`, \`name\`, \`address\`, \`phone\`, \`receipt_template\`, \`kot_enabled\`)
VALUES (1, 'Main Branch', 'City Center', '9876543210', 'format2', 1)
ON DUPLICATE KEY UPDATE \`name\`=VALUES(\`name\`);

-- Default Counter Table (For Takeaway & Coupon Orders)
INSERT INTO \`dining_tables\` (\`id\`, \`branch_id\`, \`section_id\`, \`number\`, \`display_order\`, \`status\`)
VALUES ('counter', 1, NULL, 'Counter', 0, 'vacant')
ON DUPLICATE KEY UPDATE \`number\`='Counter';

-- Default Role Permissions
INSERT INTO \`role_permissions\` (\`role\`, \`allowed_menus\`, \`locked_actions\`, \`created_at\`, \`updated_at\`)
VALUES 
('owner', '["dashboard", "tables", "pos", "quickPos", "kds", "inventory", "accounting", "captain", "liveStatus", "tokenDisplay", "qrOrdering", "crm", "cloudSaaS", "printerSettings", "utility", "ownerSettings", "rolePermissions", "reports"]', '[]', NOW(), NOW()),
('manager', '["dashboard", "tables", "pos", "quickPos", "kds", "inventory", "accounting", "captain", "liveStatus", "tokenDisplay", "qrOrdering", "crm", "cloudSaaS", "printerSettings", "ownerSettings", "reports"]', '[]', NOW(), NOW()),
('captain', '["tables", "pos", "kds", "captain", "liveStatus"]', '["delete_order", "apply_discount", "edit_prices"]', NOW(), NOW()),
('billing_staff', '["quickPos", "pos", "tables", "liveStatus", "tokenDisplay", "printerSettings"]', '["delete_order", "edit_prices"]', NOW(), NOW()),
('kitchen_staff', '["kds", "liveStatus"]', '["delete_order", "apply_discount", "edit_prices"]', NOW(), NOW())
ON DUPLICATE KEY UPDATE \`updated_at\`=NOW();

SET FOREIGN_KEY_CHECKS = 1;
`;

export default function DeveloperView() {
    const { showToast } = useApp();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);

    const parseJsonResponse = async (response) => {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const text = await response.text();
            console.error('Server non-JSON response:', text);
            throw new Error('Server API endpoint error. Please check server logs.');
        }
        return await response.json();
    };

    // Developer PIN State
    const [developerPin, setDeveloperPin] = useState('');

    // SQL DDL Schema Modal State
    const [sqlModalOpen, setSqlModalOpen] = useState(false);

    const handleCopySql = () => {
        navigator.clipboard.writeText(TENANT_DDL_SQL_SCRIPT);
        showToast('✅ Tenant DDL SQL Script copied to clipboard!');
    };

    const handleDownloadSql = () => {
        const blob = new Blob([TENANT_DDL_SQL_SCRIPT], { type: 'text/sql' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dinexa_tenant_schema_v2.4.sql';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast('💾 SQL File Downloaded!');
    };

    // Registration Form State
    const [regForm, setRegForm] = useState({
        name: '',
        subdomain: '',
        db_name: '',
        db_host: '',
        db_user: '',
        db_password: '',
        owner_name: '',
        owner_email: '',
        owner_password: '',
        menu_template: 'multi-cuisine'
    });
    const [regResult, setRegResult] = useState(null);

    // Renewal Dialog State
    const [renewOpen, setRenewOpen] = useState(false);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [renewForm, setRenewForm] = useState({
        plan_name: 'Premium Monthly',
        price: '1999',
        duration_days: '30'
    });

    // Remarks / Notes Dialog State
    const [remarksOpen, setRemarksOpen] = useState(false);
    const [remarksText, setRemarksText] = useState('');
    const [remarksLoading, setRemarksLoading] = useState(false);

    // Edit Plan Dialog State (menu visibility, expiry date, branch mode, limits)
    const [editPlanOpen, setEditPlanOpen] = useState(false);
    const [editPlanLoading, setEditPlanLoading] = useState(false);
    const [editPlanTenant, setEditPlanTenant] = useState(null);
    const [editPlanForm, setEditPlanForm] = useState({
        allowed_menus: null,  // null = all menus allowed
        is_multi_branch: false,
        plan_name: 'Premium Monthly',
        price: '1999',
        expiry_date: '',
        max_tables: 10,
        max_sections: 3,
    });

    // Edit Hotel Profile & UPI QR Dialog State
    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [editProfileLoading, setEditProfileLoading] = useState(false);
    const [editProfileTenant, setEditProfileTenant] = useState(null);
    const [editProfileForm, setEditProfileForm] = useState({
        name: '',
        address: '',
        phone: '',
        upi_id: '',
        upi_merchant_name: ''
    });

    // Tenant Error & Activity Logs Dialog State
    const [logsDialogOpen, setLogsDialogOpen] = useState(false);
    const [logsTenant, setLogsTenant] = useState(null);
    const [logsContent, setLogsContent] = useState('');
    const [logsLoading, setLogsLoading] = useState(false);

    const handleOpenLogsModal = (tenant) => {
        setLogsTenant(tenant);
        setLogsDialogOpen(true);
        fetchLogs(tenant.id);
    };

    const fetchLogs = async (tenantId) => {
        if (!tenantId) return;
        setLogsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/developer/tenants/${tenantId}/logs`);
            const data = await res.json();
            if (data.success) {
                setLogsContent(data.logs || 'No logs recorded.');
            } else {
                setLogsContent(data.error || 'Failed to fetch logs.');
            }
        } catch (err) {
            setLogsContent('Error connecting to backend logs API.');
        } finally {
            setLogsLoading(false);
        }
    };

    const handleClearLogs = async () => {
        if (!logsTenant) return;
        if (!window.confirm(`Are you sure you want to clear log file for ${logsTenant.name}?`)) return;
        setLogsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/developer/tenants/${logsTenant.id}/logs`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                showToast('Log file cleared successfully!');
                fetchLogs(logsTenant.id);
            } else {
                showToast(data.error || 'Failed to clear log file.');
            }
        } catch (err) {
            showToast('Error clearing logs.');
        } finally {
            setLogsLoading(false);
        }
    };

    // All available sidebar menu definitions for the checklist
    const MENU_OPTIONS = [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'tables', label: 'Dining Tables' },
        { id: 'pos', label: 'POS Terminal' },
        { id: 'quickPos', label: 'Quick POS' },
        { id: 'kds', label: 'Kitchen Display (KDS)' },
        { id: 'inventory', label: 'Inventory Management' },
        { id: 'accounting', label: 'Accounting & Ledger' },
        { id: 'captain', label: 'Waiter / Captain App' },
        { id: 'reports', label: 'Reports & Analytics' },
        { id: 'liveStatus', label: 'Live Status Monitor' },
        { id: 'tokenDisplay', label: 'Token Display Board' },
        { id: 'qrOrdering', label: 'QR Ordering' },
        { id: 'crm', label: 'Customer Management (CRM)' },
        { id: 'cloudSaaS', label: 'Cloud SaaS Settings' },
        { id: 'printerSettings', label: 'Printer Setup' },
        { id: 'ownerSettings', label: 'Owner Settings (Menu Config)' },
        { id: 'subscriptions', label: 'Subscriptions' },
        { id: 'franchiseHq', label: 'Franchise HQ' },
    ];

    const handleOpenEditPlan = (tenant) => {
        const currentMenus = tenant.allowed_menus;
        setEditPlanTenant(tenant);
        setEditPlanForm({
            allowed_menus: currentMenus || MENU_OPTIONS.map(m => m.id), // default: all enabled
            is_multi_branch: tenant.is_multi_branch || false,
            plan_name: tenant.subscription?.plan_name || 'Premium Monthly',
            price: tenant.subscription?.price || '1999',
            expiry_date: tenant.subscription?.end_date || '',
            max_tables: tenant.max_tables !== undefined ? tenant.max_tables : 10,
            max_sections: tenant.max_sections !== undefined ? tenant.max_sections : 3,
        });
        setEditPlanOpen(true);
    };

    const handleEditPlanMenuToggle = (menuId) => {
        setEditPlanForm(prev => {
            const current = prev.allowed_menus || [];
            const updated = current.includes(menuId)
                ? current.filter(m => m !== menuId)
                : [...current, menuId];
            return { ...prev, allowed_menus: updated };
        });
    };

    const handleEditPlanSubmit = async () => {
        if (!editPlanTenant) return;
        setEditPlanLoading(true);
        try {
            // 1. Save plan config (menus + branch mode + limits)
            const configRes = await fetch(`${API_BASE_URL}/developer/tenants/${editPlanTenant.id}/plan-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    allowed_menus: editPlanForm.allowed_menus,
                    is_multi_branch: editPlanForm.is_multi_branch,
                    max_tables: parseInt(editPlanForm.max_tables) || 10,
                    max_sections: parseInt(editPlanForm.max_sections) || 3,
                })
            });
            const configData = await parseJsonResponse(configRes);

            // 2. Save subscription expiry
            const subRes = await fetch(`${API_BASE_URL}/developer/tenants/${editPlanTenant.id}/subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    plan_name: editPlanForm.plan_name,
                    price: editPlanForm.price,
                    end_date: editPlanForm.expiry_date,
                })
            });
            const subData = await parseJsonResponse(subRes);

            if (configData.success && subData.success) {
                showToast('Plan configuration saved successfully!');
                setEditPlanOpen(false);
                fetchTenants();
            } else {
                showToast(configData.error || subData.error || 'Failed to save plan config.');
            }
        } catch (err) {
            console.error('Save plan config error:', err);
            showToast(`Error: ${err.message}`);
        } finally {
            setEditPlanLoading(false);
        }
    };

    const handleOpenEditProfile = (tenant) => {
        setEditProfileTenant(tenant);
        setEditProfileForm({
            name: tenant.name || '',
            subdomain: tenant.subdomain || '',
            address: tenant.address || tenant.branch_address || '',
            phone: tenant.phone || tenant.branch_phone || '',
            upi_id: tenant.upi_id || '',
            upi_merchant_name: tenant.upi_merchant_name || tenant.name || ''
        });
        setEditProfileOpen(true);
    };

    const handleSaveEditProfile = async () => {
        if (!editProfileTenant) return;
        setEditProfileLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${editProfileTenant.id}/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editProfileForm)
            });
            const data = await response.json();
            if (data.success) {
                showToast('Hotel profile and UPI QR updated successfully!');
                setEditProfileOpen(false);
                fetchTenants();
            } else {
                showToast(data.error || 'Failed to update hotel profile.');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`);
        } finally {
            setEditProfileLoading(false);
        }
    };

    // Branch Management States
    const [branchesOpen, setBranchesOpen] = useState(false);
    const [tenantBranches, setTenantBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [branchFormOpen, setBranchFormOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [branchForm, setBranchForm] = useState({
        name: '',
        address: '',
        phone: '',
        upi_id: '',
        upi_merchant_name: '',
        receipt_template: 'format2',
        print_feed_lines: 3
    });

    const handleManageBranches = async (tenant) => {
        setSelectedTenantId(tenant.id);
        setSelectedTenantName(tenant.name);
        setBranchesOpen(true);
        setBranchesLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${tenant.id}/branches`);
            const data = await response.json();
            if (data.success) {
                setTenantBranches(data.branches || []);
            } else {
                showToast(data.error || 'Failed to load branches.');
            }
        } catch (error) {
            showToast('Failed to connect to tenant database.');
        } finally {
            setBranchesLoading(false);
        }
    };

    const handleOpenBranchForm = (branch = null) => {
        setEditingBranch(branch);
        if (branch) {
            setBranchForm({
                name: branch.name,
                address: branch.address || '',
                phone: branch.phone || '',
                upi_id: branch.upi_id || '',
                upi_merchant_name: branch.upi_merchant_name || '',
                receipt_template: branch.receipt_template || 'format2',
                print_feed_lines: branch.print_feed_lines !== undefined && branch.print_feed_lines !== null ? branch.print_feed_lines : 3
            });
        } else {
            setBranchForm({
                name: '',
                address: '',
                phone: '',
                upi_id: '',
                upi_merchant_name: '',
                receipt_template: 'format2',
                print_feed_lines: 3
            });
        }
        setBranchFormOpen(true);
    };

    const handleBranchFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingBranch 
                ? `${API_BASE_URL}/developer/tenants/${selectedTenantId}/branches/${editingBranch.id}`
                : `${API_BASE_URL}/developer/tenants/${selectedTenantId}/branches`;
            const method = editingBranch ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(branchForm)
            });
            const data = await response.json();
            if (data.success) {
                showToast(data.message || 'Branch saved successfully!');
                setBranchFormOpen(false);
                handleManageBranches({ id: selectedTenantId, name: selectedTenantName });
            } else {
                showToast(data.error || 'Failed to save branch.');
            }
        } catch (error) {
            showToast('Network error while saving branch.');
        }
    };

    const handleDeleteBranch = async (branchId) => {
        if (!window.confirm('Are you sure you want to delete this branch?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${selectedTenantId}/branches/${branchId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                showToast('Branch deleted successfully.');
                handleManageBranches({ id: selectedTenantId, name: selectedTenantName });
            } else {
                showToast(data.error || 'Failed to delete branch.');
            }
        } catch (error) {
            showToast('Network error.');
        }
    };

    // Selective Tenant Data Reset Dialog State
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetTenant, setResetTenant] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetPinInput, setResetPinInput] = useState('');
    const [resetBranchId, setResetBranchId] = useState('');
    const [autoBackupBeforeWipe, setAutoBackupBeforeWipe] = useState(true);
    const [backupLoading, setBackupLoading] = useState(false);
    const [resetWipes, setResetWipes] = useState({
        sales_orders: true,
        kot_history: true,
        inventory_logs: true,
        accounting_ledger: true,
        customer_data: true,
        table_status: true,
        menu_catalog: false
    });

    const handleDownloadSqlBackup = async (tenant, pin = null) => {
        if (!tenant) return false;
        const p = pin || developerPin || resetPinInput || '';
        setBackupLoading(true);
        try {
            showToast(`📥 ${tenant.name} चा .sql बॅकअप डाऊनलोड सुरू झाला...`);
            const url = `${API_BASE_URL}/developer/tenants/${tenant.id}/export-backup?pin=${encodeURIComponent(p)}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/octet-stream, application/json, text/plain, */*'
                }
            });

            if (!response.ok) {
                let errorMsg = `Server Error ${response.status}: ${response.statusText}`;
                try {
                    const errData = await response.json();
                    if (errData.error) errorMsg = errData.error;
                } catch (e) {
                    const errText = await response.text();
                    if (errText && errText.length < 150) errorMsg = errText;
                }
                showToast(`❌ बॅकअप डाऊनलोड अयशस्वी: ${errorMsg}`);
                return false;
            }

            const blob = await response.blob();
            if (blob.size === 0) {
                showToast('❌ बॅकअप फाईल रिकामी (0 bytes) आहे.');
                return false;
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            const todayStr = new Date().toISOString().slice(0, 10);
            a.download = `${tenant.subdomain}_backup_${todayStr}.sql`;
            document.body.appendChild(a);
            a.click();
            
            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
                if (document.body.contains(a)) {
                    document.body.removeChild(a);
                }
            }, 2000);

            showToast(`✅ ${tenant.name} चा .sql बॅकअप यशस्वीरीत्या डाऊनलोड झाला!`);
            return true;
        } catch (e) {
            console.error('Backup download error:', e);
            showToast('बॅकअप डाऊनलोड करताना एरर आली: ' + e.message);
            return false;
        } finally {
            setBackupLoading(false);
        }
    };

    const handleOpenResetDialog = async (tenant) => {
        setResetTenant(tenant);
        setResetPinInput('');
        setResetBranchId('');
        setAutoBackupBeforeWipe(true);
        setResetWipes({
            sales_orders: true,
            kot_history: true,
            inventory_logs: true,
            accounting_ledger: true,
            customer_data: true,
            table_status: true,
            menu_catalog: false
        });
        setResetDialogOpen(true);
        // Fetch branches for this tenant to populate the dropdown
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${tenant.id}/branches`);
            const data = await response.json();
            if (data.success && Array.isArray(data.branches)) {
                setTenantBranches(data.branches);
                if (data.branches.length > 0) {
                    setResetBranchId(data.branches[0].id);
                }
            }
        } catch (error) {}
    };

    const handleConfirmDataReset = async () => {
        if (!resetTenant) return;
        if (!resetBranchId || resetBranchId === 'all') {
            showToast('⚠️ कृपया विशिष्ट Branch निवडा! "All Branches" Wipe सुरक्षेसाठी बंद आहे.');
            return;
        }
        
        const selectedWipes = Object.keys(resetWipes).filter(key => resetWipes[key]);
        if (selectedWipes.length === 0) {
            showToast('कृपया कमीत कमी १ मॉड्यूल निवडा!');
            return;
        }

        const selectedBranchName = tenantBranches.find(b => String(b.id) === String(resetBranchId))?.name || `Branch #${resetBranchId}`;
        if (!window.confirm(`⚠️ सावधान: तुम्ही "${resetTenant.name}" च्या फक्त "${selectedBranchName}" चा निवडलेला डेटा डिलीट करत आहात.\n\nतुम्हाला खात्री आहे का?`)) return;

        setResetLoading(true);
        try {
            // 1. If auto backup is enabled, trigger SQL dump download first and verify
            if (autoBackupBeforeWipe) {
                const backupSuccess = await handleDownloadSqlBackup(resetTenant, resetPinInput);
                if (!backupSuccess) {
                    showToast('⚠️ बॅकअप डाऊनलोड न झाल्यामुळे सुरक्षेसाठी Data Wipe थांबवण्यात आला आहे.');
                    setResetLoading(false);
                    return;
                }
                await new Promise(r => setTimeout(r, 1000));
            }

            // 2. Call wipe API strictly for this branch
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${resetTenant.id}/reset-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pin: resetPinInput,
                    branch_id: resetBranchId,
                    wipes: selectedWipes
                })
            });
            const data = await response.json();
            if (data.success) {
                showToast(`✅ ${resetTenant.name} (${selectedBranchName}) चा निवडलेला डेटा यशस्वीरीत्या रिसेट झाला!`);
                setResetDialogOpen(false);
            } else {
                showToast(data.error || 'Failed to reset tenant data.');
            }
        } catch (error) {
            showToast('Network error while resetting tenant data.');
        } finally {
            setResetLoading(false);
        }
    };

    // Tenant User Management (CRUD) State
    const [usersOpen, setUsersOpen] = useState(false);
    const [selectedTenantName, setSelectedTenantName] = useState('');
    const [tenantUsers, setTenantUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userFormOpen, setUserFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userForm, setUserForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'owner',
        branch_id: ''
    });

    // Fetch Tenants List
    const fetchTenants = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants`);
            const data = await response.json();
            if (data.success) {
                setTenants(data.tenants);
            } else {
                showToast('Failed to load tenants.');
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
            showToast('Unable to connect to backend server. Please verify database and server configuration.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch Developer PIN
    const fetchDeveloperPin = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/developer/settings/pin`);
            const data = await response.json();
            if (data.success) {
                setDeveloperPin(data.pin);
            }
        } catch (error) {
            console.error('Error fetching developer PIN:', error);
        }
    };

    const handleUpdatePin = async () => {
        if (!developerPin || developerPin.length < 4) {
            showToast('Developer PIN must be at least 4 digits.');
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/developer/settings/pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin: developerPin })
            });
            const data = await response.json();
            if (data.success) {
                showToast('Developer Access PIN updated successfully!');
            } else {
                showToast(data.error || 'Failed to update PIN.');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`);
        }
    };

    // Tenant User Management Handlers
    const handleManageUsers = async (tenant) => {
        setSelectedTenantId(tenant.id);
        setSelectedTenantName(tenant.name);
        setUsersOpen(true);
        setUsersLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${tenant.id}/users`);
            const data = await response.json();
            if (data.success) {
                setTenantUsers(data.users);
            } else {
                showToast(data.error || 'Failed to load tenant users.');
            }
            
            // Also fetch branches for user assignment
            const branchRes = await fetch(`${API_BASE_URL}/developer/tenants/${tenant.id}/branches`);
            const branchData = await branchRes.json();
            if (branchData.success) {
                setTenantBranches(branchData.branches || []);
            }
        } catch (error) {
            showToast('Failed to connect to tenant database.');
        } finally {
            setUsersLoading(false);
        }
    };

    const handleOpenUserForm = (user = null) => {
        setEditingUser(user);
        if (user) {
            setUserForm({
                name: user.name,
                email: user.email,
                password: '',
                role: user.role,
                branch_id: user.branch_id || ''
            });
        } else {
            setUserForm({
                name: '',
                email: '',
                password: '',
                role: 'owner',
                branch_id: ''
            });
        }
        setUserFormOpen(true);
    };

    const handleUserFormSubmit = async (e) => {
        e.preventDefault();
        if (!editingUser && !userForm.password) {
            showToast('Password is required for new users.');
            return;
        }
        const url = editingUser 
            ? `${API_BASE_URL}/developer/tenants/${selectedTenantId}/users/${editingUser.id}`
            : `${API_BASE_URL}/developer/tenants/${selectedTenantId}/users`;
        const method = editingUser ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userForm)
            });
            const data = await response.json();
            if (data.success) {
                showToast(editingUser ? 'User updated successfully!' : 'User created successfully!');
                setUserFormOpen(false);
                handleManageUsers({ id: selectedTenantId, name: selectedTenantName });
            } else {
                showToast(data.error || 'Failed to save user.');
            }
        } catch (error) {
            showToast('Network error.');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${selectedTenantId}/users/${userId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                showToast('User deleted successfully.');
                handleManageUsers({ id: selectedTenantId, name: selectedTenantName });
            } else {
                showToast(data.error || 'Failed to delete user.');
            }
        } catch (error) {
            showToast('Network error.');
        }
    };

    useEffect(() => {
        fetchTenants();
        fetchDeveloperPin();
    }, []);

    // Handle Tenant Registration
    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setRegResult(null);
        try {
            const response = await fetch(`${API_BASE_URL}/landlord/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(regForm)
            });
            const data = await response.json();
            if (data.success) {
                showToast('Tenant Registered Successfully!');
                setRegResult({
                    success: true,
                    message: data.message,
                    setup: data.setup_status
                });
                setRegForm({
                    name: '',
                    subdomain: '',
                    db_name: '',
                    db_host: '',
                    db_user: '',
                    db_password: '',
                    owner_name: '',
                    owner_email: '',
                    owner_password: '',
                    menu_template: 'multi-cuisine'
                });
                fetchTenants();
            } else {
                setRegResult({
                    success: false,
                    message: data.error || 'Registration failed.'
                });
                showToast('Registration failed.');
            }
        } catch (error) {
            setRegResult({
                success: false,
                message: 'Failed to connect to backend register endpoint.'
            });
            showToast('Connection error.');
        }
    };

    // Update Status
    const handleStatusChange = async (id, status) => {
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${id}/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            const data = await response.json();
            if (data.success) {
                showToast(`Tenant status updated to ${status}`);
                fetchTenants();
            } else {
                showToast(data.error || 'Failed to update status.');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`);
        }
    };

    // Update Module Toggle
    const handleModuleToggle = async (tenantId, moduleName, currentVal, modulesList = []) => {
        let exists = false;
        const updatedModules = (modulesList || []).map(mod => {
            if (mod.module_name === moduleName) {
                exists = true;
                return { name: moduleName, enabled: !currentVal };
            }
            return { name: mod.module_name, enabled: mod.is_enabled === 1 || mod.is_enabled === true };
        });

        if (!exists) {
            updatedModules.push({ name: moduleName, enabled: !currentVal });
        }

        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${tenantId}/modules`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ modules: updatedModules })
            });
            const data = await parseJsonResponse(response);
            if (data.success) {
                showToast('Add-on modules updated successfully!');
                fetchTenants();
            } else {
                showToast(data.error || 'Failed to toggle module.');
            }
        } catch (error) {
            console.error('Module toggle error:', error);
            showToast(`Error: ${error.message}`);
        }
    };

    // Handle Remarks Save
    const handleSaveRemarks = async () => {
        if (!selectedTenantId) return;
        setRemarksLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${selectedTenantId}/remarks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ remarks: remarksText })
            });
            const data = await response.json();
            if (data.success) {
                showToast('Notes saved successfully!');
                setRemarksOpen(false);
                fetchTenants();
            } else {
                showToast(data.error || 'Failed to save notes.');
            }
        } catch (error) {
            showToast(`Data Reset Error: ${error.message}`);
        } finally {
            setResetLoading(false);
        }
    };

    // Fresh Migration & Seeding States & Handlers
    const [freshMigrateOpen, setFreshMigrateOpen] = useState(false);
    const [freshMigrateLoading, setFreshMigrateLoading] = useState(false);
    const [freshMigrateTenant, setFreshMigrateTenant] = useState(null);
    const [freshMigratePin, setFreshMigratePin] = useState('');
    const [freshDbUser, setFreshDbUser] = useState('');
    const [freshDbPass, setFreshDbPass] = useState('');

    const handleOpenFreshMigrate = (tenant) => {
        setFreshMigrateTenant(tenant);
        setFreshMigratePin('');
        setFreshDbUser(tenant.db_user && !tenant.db_user.includes('@') ? tenant.db_user : '');
        setFreshDbPass(tenant.db_password || '');
        setFreshMigrateOpen(true);
    };

    const handleConfirmFreshMigrate = async () => {
        if (!freshMigrateTenant) return;
        setFreshMigrateLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${freshMigrateTenant.id}/fresh-migrate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    pin: freshMigratePin,
                    db_user: freshDbUser,
                    db_password: freshDbPass
                })
            });

            const rawText = await response.text();
            let data = {};
            try {
                data = JSON.parse(rawText);
            } catch (e) {
                console.error("Non-JSON Server Response:", rawText);
                const cleanMsg = rawText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                const displayMsg = cleanMsg.length > 150 ? cleanMsg.substring(0, 150) + '...' : cleanMsg;
                showToast(`Server Response: ${displayMsg}`);
                return;
            }

            if (data.success) {
                showToast(`✅ ${data.message || 'Fresh database migration & seeding completed!'}`);
                setFreshMigrateOpen(false);
                fetchTenants();
            } else {
                showToast(data.error || 'Fresh migration failed.');
            }
        } catch (err) {
            showToast(`Network Error: ${err.message}`);
        } finally {
            setFreshMigrateLoading(false);
        }
    };

    // Handle Subscription Renewal
    const handleRenewSubmit = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/developer/tenants/${selectedTenantId}/subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    plan_name: renewForm.plan_name,
                    price: parseFloat(renewForm.price),
                    duration_days: parseInt(renewForm.duration_days)
                })
            });
            const data = await response.json();
            if (data.success) {
                showToast('Subscription renewed successfully!');
                setRenewOpen(false);
                fetchTenants();
            } else {
                showToast('Failed to renew subscription.');
            }
        } catch (error) {
            showToast('Network error.');
        }
    };

    return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Top Stat Cards & Developer Settings */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: '#008751', color: 'white', borderRadius: 3 }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyBetween: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Tenants</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 900 }}>{tenants.length}</Typography>
                            </Box>
                            <BusinessIcon sx={{ fontSize: 60, opacity: 0.3, ml: 'auto' }} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: '#10b981', color: 'white', borderRadius: 3 }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyBetween: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>Active Subscriptions</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 900 }}>
                                    {tenants.filter(t => t.status === 'active').length}
                                </Typography>
                            </Box>
                            <SettingsIcon sx={{ fontSize: 60, opacity: 0.3, ml: 'auto' }} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>
                                Developer Access PIN
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    type="password"
                                    value={developerPin}
                                    onChange={(e) => setDeveloperPin(e.target.value)}
                                    inputProps={{ maxLength: 4, style: { letterSpacing: '0.25em', fontWeight: 'bold', textAlign: 'center' } }}
                                    sx={{ maxWidth: 110 }}
                                />
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={handleUpdatePin}
                                    sx={{ fontWeight: 800, height: 40 }}
                                >
                                    Update PIN
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Registration Form & Result Alert */}
            <Grid container spacing={4}>
                <Grid item xs={12} md={7}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon color="primary" /> Register New SaaS Tenant
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<StorageIcon />}
                                    onClick={() => setSqlModalOpen(true)}
                                    sx={{ 
                                        fontWeight: 800, 
                                        textTransform: 'none', 
                                        borderRadius: 2, 
                                        bgcolor: '#0284c7', 
                                        '&:hover': { bgcolor: '#0369a1' } 
                                    }}
                                >
                                    📋 Copy Tenant SQL Script (Hostinger DDL)
                                </Button>
                            </Box>
                            <form onSubmit={handleRegisterSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Restaurant / Client Name"
                                            value={regForm.name}
                                            onChange={(e) => setRegForm({...regForm, name: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Subdomain (e.g. swad)"
                                            value={regForm.subdomain}
                                            onChange={(e) => setRegForm({...regForm, subdomain: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Owner Name"
                                            value={regForm.owner_name}
                                            onChange={(e) => setRegForm({...regForm, owner_name: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            type="email"
                                            label="Owner Email Address"
                                            value={regForm.owner_email}
                                            onChange={(e) => setRegForm({...regForm, owner_email: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            type="password"
                                            label="Owner Access Password"
                                            value={regForm.owner_password}
                                            onChange={(e) => setRegForm({...regForm, owner_password: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Menu Seeding Preset Template</InputLabel>
                                            <Select
                                                value={regForm.menu_template}
                                                onChange={(e) => setRegForm({...regForm, menu_template: e.target.value})}
                                                label="Menu Seeding Preset Template"
                                            >
                                                <MenuItem value="multi-cuisine">Multi-Cuisine (Standard Chinese, Tandoor, Indian, Beverages)</MenuItem>
                                                <MenuItem value="thali-special">Thali Special (Maharashtrian Special Veg/Non-Veg, Sweets)</MenuItem>
                                                <MenuItem value="blank">Blank (Initialize with empty categories and items)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1, color: '#475569' }}>
                                            Database Settings (Optional - Local vs Online defaults apply)
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Custom Database Name"
                                            value={regForm.db_name}
                                            onChange={(e) => setRegForm({...regForm, db_name: e.target.value})}
                                            helperText="Local: dx_subdomain | Hostinger: u905726705_dx_subdomain"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Database Host"
                                            value={regForm.db_host}
                                            placeholder="127.0.0.1"
                                            onChange={(e) => setRegForm({...regForm, db_host: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Database User"
                                            value={regForm.db_user}
                                            placeholder="root"
                                            onChange={(e) => setRegForm({...regForm, db_user: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="password"
                                            label="Database Password"
                                            value={regForm.db_password}
                                            onChange={(e) => setRegForm({...regForm, db_password: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button type="submit" variant="contained" color="primary" sx={{ fontWeight: 800 }}>
                                            Register & Setup Tenant Database
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                    {regResult && (
                        <Alert 
                            severity={regResult.success ? 'success' : 'error'} 
                            sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                        >
                            <Typography sx={{ fontWeight: 800 }}>
                                {regResult.success ? 'Success!' : 'Error'}
                            </Typography>
                            <Typography variant="body2" sx={{ my: 1 }}>
                                {regResult.message}
                            </Typography>
                            {regResult.success && regResult.setup && (
                                <Box sx={{ mt: 1, fontSize: '0.8rem' }}>
                                    <div>Environment: {regResult.setup.local_env ? 'Localhost' : 'Online Server'}</div>
                                    <div>Database Created: {regResult.setup.database_created ? '✔️ Yes' : '⚠️ No (Create Manually)'}</div>
                                    <div>Migrations Run: {regResult.setup.migrations_run ? '✔️ Yes' : '⚠️ No'}</div>
                                    <div>Owner Account Seeded: {regResult.setup.owner_seeded ? '✔️ Yes' : '⚠️ No'}</div>
                                </Box>
                            )}
                        </Alert>
                    )}
                </Grid>
            </Grid>

            {/* Tenants Management Table */}
            <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Registered Tenant Accounts
                        </Typography>
                        <Button variant="outlined" size="small" onClick={fetchTenants} sx={{ fontWeight: 700 }}>
                            Refresh List
                        </Button>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="primary" />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table>
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>Tenant Info</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Subscription</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Add-on Modules (Features)</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Notes</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tenants.map((tenant) => {
                                        const complianceMod = tenant.modules?.find(m => m.module_name === 'compliance');
                                        const franchiseMod = tenant.modules?.find(m => m.module_name === 'franchise');
                                        const accountingMod = tenant.modules?.find(m => m.module_name === 'accounting');
                                        const inventoryMod = tenant.modules?.find(m => m.module_name === 'inventory');
                                        const zomatoSwiggyMod = tenant.modules?.find(m => m.module_name === 'zomato_swiggy');
                                        const kdsMod = tenant.modules?.find(m => m.module_name === 'kds');
                                        const captainMod = tenant.modules?.find(m => m.module_name === 'captain');
                                        const menuCsvMod = tenant.modules?.find(m => m.module_name === 'menu_csv_export_import');
                                        const bizAnalyticsMod = tenant.modules?.find(m => m.module_name === 'bizAnalytics');

                                        return (
                                            <TableRow key={tenant.id} hover>
                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                        {tenant.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                        Subdomain: {tenant.subdomain}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                        DB: {tenant.db_name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {tenant.subscription ? (
                                                        <Box>
                                                            <Chip 
                                                                label={tenant.subscription.plan_name} 
                                                                size="small" 
                                                                color="primary" 
                                                                sx={{ fontWeight: 700, mb: 0.5 }} 
                                                            />
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#475569' }}>
                                                                Expires: {tenant.subscription.end_date}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Chip label="No Subscription" size="small" color="error" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <FormControl size="small">
                                                        <Select
                                                            value={tenant.status}
                                                            onChange={(e) => handleStatusChange(tenant.id, e.target.value)}
                                                            sx={{ 
                                                                fontWeight: 700, 
                                                                height: 32,
                                                                color: tenant.status === 'active' ? '#008751' : '#e11d48'
                                                            }}
                                                        >
                                                            <MenuItem value="active">Active</MenuItem>
                                                            <MenuItem value="inactive">Inactive</MenuItem>
                                                            <MenuItem value="suspended">Suspended</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell>
                                                    <Grid container spacing={1} sx={{ maxWidth: 350 }}>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        checked={complianceMod?.is_enabled === 1 || complianceMod?.is_enabled === true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'compliance', complianceMod?.is_enabled === 1 || complianceMod?.is_enabled === true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption">Compliance</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        checked={franchiseMod?.is_enabled === 1 || franchiseMod?.is_enabled === true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'franchise', franchiseMod?.is_enabled === 1 || franchiseMod?.is_enabled === true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption">Franchise</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        checked={accountingMod?.is_enabled === 1 || accountingMod?.is_enabled === true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'accounting', accountingMod?.is_enabled === 1 || accountingMod?.is_enabled === true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption">Accounting</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        checked={inventoryMod?.is_enabled === 1 || inventoryMod?.is_enabled === true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'inventory', inventoryMod?.is_enabled === 1 || inventoryMod?.is_enabled === true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption">Inventory</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        color="success"
                                                                        checked={kdsMod ? (kdsMod.is_enabled === 1 || kdsMod.is_enabled === true) : true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'kds', kdsMod ? (kdsMod.is_enabled === 1 || kdsMod.is_enabled === true) : true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption" sx={{ fontWeight: 700, color: '#059669' }}>KOT & KDS</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        color="primary"
                                                                        checked={captainMod ? (captainMod.is_enabled === 1 || captainMod.is_enabled === true) : true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'captain', captainMod ? (captainMod.is_enabled === 1 || captainMod.is_enabled === true) : true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption" sx={{ fontWeight: 700, color: '#0284c7' }}>Captain App</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        color="secondary"
                                                                        checked={zomatoSwiggyMod?.is_enabled === 1 || zomatoSwiggyMod?.is_enabled === true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'zomato_swiggy', zomatoSwiggyMod?.is_enabled === 1 || zomatoSwiggyMod?.is_enabled === true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption" sx={{ fontWeight: 700, color: '#ea580c' }}>Zomato & Swiggy</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        color="success"
                                                                        checked={menuCsvMod ? (menuCsvMod.is_enabled === 1 || menuCsvMod.is_enabled === true) : true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'menu_csv_export_import', menuCsvMod ? (menuCsvMod.is_enabled === 1 || menuCsvMod.is_enabled === true) : true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption" sx={{ fontWeight: 700, color: '#008751' }}>Menu CSV Ex/Imp</Typography>}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch 
                                                                        size="small" 
                                                                        color="info"
                                                                        checked={bizAnalyticsMod?.is_enabled === 1 || bizAnalyticsMod?.is_enabled === true} 
                                                                        onChange={() => handleModuleToggle(tenant.id, 'bizAnalytics', bizAnalyticsMod?.is_enabled === 1 || bizAnalyticsMod?.is_enabled === true, tenant.modules)}
                                                                    />
                                                                }
                                                                label={<Typography variant="caption" sx={{ fontWeight: 700, color: '#0284c7' }}>Dinexa Biz</Typography>}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 180 }}>
                                                    {tenant.remarks ? (
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                color: '#475569', 
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                lineHeight: 1.4
                                                            }}
                                                        >
                                                            {tenant.remarks}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                                            No notes
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                            <Button 
                                                                variant="contained" 
                                                                size="small" 
                                                                onClick={() => handleOpenEditPlan(tenant)}
                                                                sx={{ fontWeight: 700, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}
                                                            >
                                                                ✏️ Edit Plan
                                                            </Button>
                                                            <Button 
                                                                variant="contained" 
                                                                size="small" 
                                                                onClick={() => handleOpenEditProfile(tenant)}
                                                                sx={{ fontWeight: 700, bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' } }}
                                                            >
                                                                ✏️ Profile & QR
                                                            </Button>
                                                            <Button 
                                                                variant="outlined" 
                                                                size="small" 
                                                                onClick={() => handleManageBranches(tenant)}
                                                                sx={{ fontWeight: 700, borderColor: '#0284c7', color: '#0284c7' }}
                                                            >
                                                                🏢 Branches
                                                            </Button>
                                                            <Button 
                                                                variant="outlined" 
                                                                size="small" 
                                                                onClick={() => handleManageUsers(tenant)}
                                                                sx={{ fontWeight: 700 }}
                                                            >
                                                                Manage Users
                                                            </Button>
                                                            <Button 
                                                                variant="contained" 
                                                                size="small" 
                                                                onClick={() => handleOpenFreshMigrate(tenant)}
                                                                sx={{ fontWeight: 800, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                                                            >
                                                                ⚡ Fresh Migrate DB
                                                            </Button>
                                                            <Button 
                                                                variant="contained" 
                                                                size="small" 
                                                                onClick={() => handleOpenLogsModal(tenant)}
                                                                sx={{ fontWeight: 800, bgcolor: '#0f172a', color: '#38bdf8', '&:hover': { bgcolor: '#1e293b' } }}
                                                            >
                                                                📄 Logs (लॉग्स)
                                                            </Button>
                                                            <Button 
                                                                variant="outlined" 
                                                                color="primary" 
                                                                size="small" 
                                                                onClick={() => handleDownloadSqlBackup(tenant, developerPin)}
                                                                sx={{ fontWeight: 800, borderColor: '#3b82f6', color: '#1d4ed8' }}
                                                            >
                                                                📥 Backup DB (.sql)
                                                            </Button>
                                                            <Button 
                                                                variant="outlined" 
                                                                color="error" 
                                                                size="small" 
                                                                onClick={() => handleOpenResetDialog(tenant)}
                                                                sx={{ fontWeight: 800 }}
                                                            >
                                                                🗑️ Reset Data
                                                            </Button>
                                                        </Box>
                                                        <Button 
                                                            variant="text" 
                                                            size="small" 
                                                            onClick={() => {
                                                                setSelectedTenantId(tenant.id);
                                                                setRemarksText(tenant.remarks || '');
                                                                setRemarksOpen(true);
                                                            }}
                                                            sx={{ fontWeight: 700, color: '#64748b', justifyContent: 'flex-start', pl: 0 }}
                                                        >
                                                            📝 Edit Notes
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Edit Tenant Notes / Remarks Dialog */}
            <Dialog open={remarksOpen} onClose={() => setRemarksOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Edit Internal Notes</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                        These notes are internal to the Developer Dashboard and are not visible to the tenant.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Developer Notes / Remarks"
                        placeholder="e.g. Contact: 9876543210 | Payment due on 1st. Special menu config applied."
                        value={remarksText}
                        onChange={(e) => setRemarksText(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRemarksOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button 
                        onClick={handleSaveRemarks} 
                        variant="contained" 
                        disabled={remarksLoading}
                        sx={{ fontWeight: 800 }}
                    >
                        {remarksLoading ? 'Saving...' : 'Save Notes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ==================== EDIT PLAN DIALOG ==================== */}
            <Dialog open={editPlanOpen} onClose={() => setEditPlanOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #e2e8f0', pb: 2 }}>
                    ✏️ Edit Plan — {editPlanTenant?.name}
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 400 }}>
                        Configure subscription, branch mode, and which menu items this tenant can access.
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={3}>
                        {/* LEFT: Subscription & Branch */}
                        <Grid item xs={12} md={5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>
                                📅 Subscription Details
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Plan Name"
                                    value={editPlanForm.plan_name}
                                    onChange={(e) => setEditPlanForm(p => ({...p, plan_name: e.target.value}))}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Plan Price (₹)"
                                    type="number"
                                    value={editPlanForm.price}
                                    onChange={(e) => setEditPlanForm(p => ({...p, price: e.target.value}))}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Plan Expiry Date"
                                    type="date"
                                    value={editPlanForm.expiry_date}
                                    onChange={(e) => setEditPlanForm(p => ({...p, expiry_date: e.target.value}))}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Set the exact expiry date for this tenant's plan"
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Max Tables Limit"
                                    type="number"
                                    value={editPlanForm.max_tables}
                                    onChange={(e) => setEditPlanForm(p => ({...p, max_tables: e.target.value}))}
                                    helperText="Max allowed dining tables inside tenant database"
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Max Sections Limit"
                                    type="number"
                                    value={editPlanForm.max_sections}
                                    onChange={(e) => setEditPlanForm(p => ({...p, max_sections: e.target.value}))}
                                    helperText="Max allowed dining sections inside tenant database"
                                />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
                                🏢 Branch Mode
                            </Typography>
                            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2, border: '1px solid #e2e8f0' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editPlanForm.is_multi_branch}
                                            onChange={(e) => setEditPlanForm(p => ({...p, is_multi_branch: e.target.checked}))}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography sx={{ fontWeight: 700 }}>
                                                {editPlanForm.is_multi_branch ? 'Multi-Branch Enabled' : 'Single Branch Mode'}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                {editPlanForm.is_multi_branch
                                                    ? 'Tenant can manage multiple branches with separate P&L.'
                                                    : 'Only one branch/location active for this tenant.'}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>

                        {/* RIGHT: Menu Access Control */}
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    🔒 Menu Access Control
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button 
                                        size="small" 
                                        onClick={() => setEditPlanForm(p => ({...p, allowed_menus: MENU_OPTIONS.map(m => m.id)}))}
                                        sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                    >
                                        Select All
                                    </Button>
                                    <Button 
                                        size="small" 
                                        color="error"
                                        onClick={() => setEditPlanForm(p => ({...p, allowed_menus: []}))}
                                        sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                    >
                                        Clear All
                                    </Button>
                                </Box>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2 }}>
                                Only checked items will appear in this tenant's sidebar navigation.
                            </Typography>
                            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', p: 2, maxHeight: 360, overflowY: 'auto' }}>
                                <FormGroup>
                                    <Grid container spacing={0}>
                                        {MENU_OPTIONS.map((menu) => (
                                            <Grid item xs={12} sm={6} key={menu.id}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={(editPlanForm.allowed_menus || []).includes(menu.id)}
                                                            onChange={() => handleEditPlanMenuToggle(menu.id)}
                                                            sx={{ color: '#008751', '&.Mui-checked': { color: '#008751' } }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                                            {menu.label}
                                                        </Typography>
                                                    }
                                                    sx={{ mb: 0.5 }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </FormGroup>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#008751', fontWeight: 700, mt: 1, display: 'block' }}>
                                {(editPlanForm.allowed_menus || []).length}/{MENU_OPTIONS.length} menus enabled
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setEditPlanOpen(false)} sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleEditPlanSubmit}
                        disabled={editPlanLoading}
                        sx={{ fontWeight: 800, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}
                    >
                        {editPlanLoading ? 'Saving...' : 'Save Plan Configuration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Renew Subscription Dialog */}
            <Dialog open={renewOpen} onClose={() => setRenewOpen(false)}>
                <DialogTitle sx={{ fontWeight: 800 }}>Renew / Change Plan</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, width: 280 }}>
                        <TextField
                            label="Plan Name"
                            size="small"
                            value={renewForm.plan_name}
                            onChange={(e) => setRenewForm({...renewForm, plan_name: e.target.value})}
                        />
                        <TextField
                            label="Price (INR)"
                            type="number"
                            size="small"
                            value={renewForm.price}
                            onChange={(e) => setRenewForm({...renewForm, price: e.target.value})}
                        />
                        <TextField
                            label="Duration (Days)"
                            type="number"
                            size="small"
                            value={renewForm.duration_days}
                            onChange={(e) => setRenewForm({...renewForm, duration_days: e.target.value})}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRenewOpen(false)} sx={{ fontWeight: 700 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleRenewSubmit} variant="contained" sx={{ fontWeight: 800 }}>
                        Renew
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Tenant Users Dialog */}
            <Dialog open={usersOpen} onClose={() => setUsersOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Manage Users - {selectedTenantName}</span>
                    <Button variant="contained" size="small" onClick={() => handleOpenUserForm()} sx={{ fontWeight: 800 }}>
                        Add Tenant User
                    </Button>
                </DialogTitle>
                <DialogContent dividers>
                    {usersLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="primary" />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table>
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>User Name</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Email Address</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>System Role</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tenantUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#64748b' }}>
                                                No users configured in this tenant database yet.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        tenantUsers.map((user) => (
                                            <TableRow key={user.id} hover>
                                                <TableCell sx={{ fontWeight: 700 }}>{user.name}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={user.role.toUpperCase()} 
                                                        size="small" 
                                                        color={user.role === 'owner' ? 'primary' : 'secondary'} 
                                                        sx={{ fontWeight: 700 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small" 
                                                            onClick={() => handleOpenUserForm(user)}
                                                            sx={{ fontWeight: 700 }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button 
                                                            variant="outlined" 
                                                            color="error" 
                                                            size="small" 
                                                            onClick={() => handleDeleteUser(user.id)}
                                                            sx={{ fontWeight: 700 }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setUsersOpen(false)} sx={{ fontWeight: 700 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add / Edit Tenant User Dialog */}
            <Dialog open={userFormOpen} onClose={() => setUserFormOpen(false)}>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editingUser ? 'Edit Tenant User' : 'Add Tenant User'}
                </DialogTitle>
                <form onSubmit={handleUserFormSubmit}>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1, width: 320 }}>
                            <TextField
                                required
                                label="Full Name"
                                size="small"
                                value={userForm.name}
                                onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                            />
                            <TextField
                                required
                                label="Email Address"
                                type="email"
                                size="small"
                                value={userForm.email}
                                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                            />
                            <TextField
                                required={!editingUser}
                                label="Access Password"
                                type="password"
                                size="small"
                                placeholder={editingUser ? "(Unchanged if empty)" : ""}
                                value={userForm.password}
                                onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>System Role</InputLabel>
                                <Select
                                    value={userForm.role}
                                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                                    label="System Role"
                                >
                                    <MenuItem value="owner">Owner (Full Store Admin)</MenuItem>
                                    <MenuItem value="manager">Manager (Menu & Shift Operations)</MenuItem>
                                    <MenuItem value="captain">Captain (POS/Ordering App)</MenuItem>
                                    <MenuItem value="billing">Billing (Cash Counter Terminal)</MenuItem>
                                    <MenuItem value="kitchen">Kitchen (KDS Screen User)</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Assign to Branch</InputLabel>
                                <Select
                                    value={userForm.branch_id || ''}
                                    onChange={(e) => setUserForm({...userForm, branch_id: e.target.value})}
                                    label="Assign to Branch"
                                >
                                    <MenuItem value="">All Branches / Main Admin</MenuItem>
                                    {tenantBranches.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setUserFormOpen(false)} sx={{ fontWeight: 700 }}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" sx={{ fontWeight: 800 }}>
                            Save User
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Edit Hotel Profile & Dynamic UPI QR Code Dialog */}
            <Dialog open={editProfileOpen} onClose={() => setEditProfileOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 900, bgcolor: '#0284c7', color: '#ffffff' }}>
                    ✏️ Edit Hotel Profile & Dynamic UPI QR Code ({editProfileTenant?.name})
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Alert severity="info" sx={{ fontWeight: 600 }}>
                            या सेटिंग्ज बदलल्यास बिलावर दिसणारे **हॉटेलचे नाव**, **पत्ता**, **फोन नंबर** आणि **UPI QR Code (GPay/PhonePe)** ऑनलाईन अपडेट होईल.
                        </Alert>

                        <TextField
                            fullWidth
                            label="Hotel / Restaurant Name (हॉटेलचे नाव)"
                            value={editProfileForm.name}
                            onChange={(e) => setEditProfileForm({ ...editProfileForm, name: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            label="POS Subdomain Code (उदा. swad किंवा hotel1)"
                            value={editProfileForm.subdomain}
                            onChange={(e) => setEditProfileForm({ ...editProfileForm, subdomain: e.target.value.toLowerCase().trim() })}
                            helperText="हा सबडोमेन कोड टाकून ग्राहक POS सिस्टीम लॉगिन करतील."
                        />

                        <TextField
                            fullWidth
                            label="Address (हॉटेलचा पत्ता)"
                            value={editProfileForm.address}
                            onChange={(e) => setEditProfileForm({ ...editProfileForm, address: e.target.value })}
                            multiline
                            rows={2}
                        />

                        <TextField
                            fullWidth
                            label="Contact Phone Number (मोबाईल नंबर)"
                            value={editProfileForm.phone}
                            onChange={(e) => setEditProfileForm({ ...editProfileForm, phone: e.target.value })}
                        />

                        <Divider sx={{ my: 1 }}>
                            <Chip label="Dynamic Bill UPI QR Code Config" color="primary" size="small" sx={{ fontWeight: 800 }} />
                        </Divider>

                        <TextField
                            fullWidth
                            label="UPI VPA ID (उदा. 9876543210@ybl किंवा hotel@upi)"
                            value={editProfileForm.upi_id}
                            onChange={(e) => setEditProfileForm({ ...editProfileForm, upi_id: e.target.value })}
                            helperText="बिलावरील QR कोड स्कॅन केल्यावर या UPI ID वर थेट पैसे जमा होतील."
                        />

                        <TextField
                            fullWidth
                            label="UPI Merchant Name (क्यूआर कोड वर दिसणारे नाव)"
                            value={editProfileForm.upi_merchant_name}
                            onChange={(e) => setEditProfileForm({ ...editProfileForm, upi_merchant_name: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setEditProfileOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSaveEditProfile}
                        disabled={editProfileLoading}
                        sx={{ fontWeight: 800, bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' }, borderRadius: 2 }}
                    >
                        {editProfileLoading ? 'Saving...' : 'Save Profile & UPI QR'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Tenant Branches Dialog */}
            <Dialog open={branchesOpen} onClose={() => setBranchesOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800, bgcolor: '#0284c7', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>🏢 Manage Outlets & Branches ({selectedTenantName})</span>
                    <Button variant="contained" size="small" onClick={() => handleOpenBranchForm()} sx={{ fontWeight: 800, bgcolor: '#ffffff', color: '#0284c7', '&:hover': { bgcolor: '#f1f5f9' } }}>
                        + Add New Branch
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {branchesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="primary" />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mt: 2 }}>
                            <Table>
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>ID</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Branch Name</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Address & Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Thermal Template</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>UPI ID</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tenantBranches.map((b) => (
                                        <TableRow key={b.id} hover>
                                            <TableCell sx={{ fontWeight: 800 }}>#{b.id}</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>{b.name}</TableCell>
                                            <TableCell sx={{ fontSize: '0.8rem', color: '#475569' }}>
                                                {b.address || '-'}<br />
                                                {b.phone ? `Ph: ${b.phone}` : ''}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{b.receipt_template || 'format2'}</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#0284c7', fontSize: '0.8rem' }}>{b.upi_id || '-'}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button variant="outlined" size="small" onClick={() => handleOpenBranchForm(b)} sx={{ fontWeight: 700 }}>
                                                        Edit
                                                    </Button>
                                                    {b.id !== 1 && (
                                                        <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteBranch(b.id)} sx={{ fontWeight: 700 }}>
                                                            Delete
                                                        </Button>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setBranchesOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add / Edit Branch Dialog */}
            <Dialog open={branchFormOpen} onClose={() => setBranchFormOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editingBranch ? `Edit Branch #${editingBranch.id}` : 'Add New Branch'}
                </DialogTitle>
                <form onSubmit={handleBranchFormSubmit}>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                required
                                label="Branch Outlet Name"
                                size="small"
                                value={branchForm.name}
                                onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
                            />
                            <TextField
                                label="Address"
                                size="small"
                                multiline
                                rows={2}
                                value={branchForm.address}
                                onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                            />
                            <TextField
                                label="Contact Phone"
                                size="small"
                                value={branchForm.phone}
                                onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                            />
                            <TextField
                                label="Branch UPI VPA ID (e.g. branch@upi)"
                                size="small"
                                value={branchForm.upi_id}
                                onChange={(e) => setBranchForm({ ...branchForm, upi_id: e.target.value })}
                            />
                            <TextField
                                label="UPI Merchant Name"
                                size="small"
                                value={branchForm.upi_merchant_name}
                                onChange={(e) => setBranchForm({ ...branchForm, upi_merchant_name: e.target.value })}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Thermal Bill Template</InputLabel>
                                <Select
                                    value={branchForm.receipt_template}
                                    onChange={(e) => setBranchForm({ ...branchForm, receipt_template: e.target.value })}
                                    label="Thermal Bill Template"
                                >
                                    <MenuItem value="coupon1">Coupon 1 (Snack Token Box - शिफारस केलेले)</MenuItem>
                                    <MenuItem value="coupon2">Coupon 2 (Compact Centered Token)</MenuItem>
                                    <MenuItem value="format1">Format 1 (Compact 58mm)</MenuItem>
                                    <MenuItem value="format2">Format 2 (Standard 58mm/80mm)</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Paper Feed Lines (कागद लाईन फीड)</InputLabel>
                                <Select
                                    value={branchForm.print_feed_lines ?? 3}
                                    onChange={(e) => setBranchForm({ ...branchForm, print_feed_lines: parseInt(e.target.value, 10) })}
                                    label="Paper Feed Lines (कागद लाईन फीड)"
                                >
                                    <MenuItem value={0}>0 Lines (नो फीड)</MenuItem>
                                    <MenuItem value={1}>1 Line</MenuItem>
                                    <MenuItem value={2}>2 Lines</MenuItem>
                                    <MenuItem value={3}>3 Lines (Standard)</MenuItem>
                                    <MenuItem value={4}>4 Lines</MenuItem>
                                    <MenuItem value={5}>5 Lines (शिफारस केलेले)</MenuItem>
                                    <MenuItem value={6}>6 Lines</MenuItem>
                                    <MenuItem value={7}>7 Lines</MenuItem>
                                    <MenuItem value={8}>8 Lines</MenuItem>
                                    <MenuItem value={9}>9 Lines</MenuItem>
                                    <MenuItem value={10}>10 Lines (Extra Space)</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setBranchFormOpen(false)} sx={{ fontWeight: 700 }}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" sx={{ fontWeight: 800, bgcolor: '#0284c7' }}>
                            Save Branch
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Selective Tenant Data Reset Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 900, bgcolor: '#b91c1c', color: '#ffffff' }}>
                    🗑️ Selective Tenant Data Wipe & Reset ({resetTenant?.name})
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="warning" sx={{ fontWeight: 700 }}>
                            **सावधान**: निवडलेला डेटा कायमचा डिलीट केला जाईल. हॉटेल ओनरला फ्रेश सुरुवात करून देण्यासाठीच याचा वापर करा.
                        </Alert>

                        <FormControl fullWidth size="small" required error={!resetBranchId}>
                            <InputLabel sx={{ fontWeight: 800 }}>Select Target Branch (विशिष्ट ब्रँच निवडा) *</InputLabel>
                            <Select
                                value={resetBranchId}
                                label="Select Target Branch (विशिष्ट ब्रँच निवडा) *"
                                onChange={(e) => setResetBranchId(e.target.value)}
                                sx={{ fontWeight: 800, borderRadius: 2 }}
                            >
                                {tenantBranches.length === 0 ? (
                                    <MenuItem value="" disabled sx={{ fontWeight: 700 }}>
                                        कोणतीही ब्रँच उपलब्ध नाही (Loading...)
                                    </MenuItem>
                                ) : (
                                    tenantBranches.map((b) => (
                                        <MenuItem key={b.id} value={b.id} sx={{ fontWeight: 800 }}>
                                            🏢 Branch #{b.id}: {b.name} {b.id === 1 ? '(Main Branch)' : ''}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2 }}>
                            <FormControlLabel
                                control={<Checkbox checked={autoBackupBeforeWipe} onChange={(e) => setAutoBackupBeforeWipe(e.target.checked)} color="primary" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800, color: '#1e40af' }}>🛡️ Wipe करण्यापूर्वी संपूर्ण DB चा .sql बॅकअप डाऊनलोड करा (Download SQL Backup first)</Typography>}
                            />
                        </Paper>

                        <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#334155', mt: 0.5 }}>
                            डिलीट करायचा डेटा निवडा (Select Modules to Wipe):
                        </Typography>

                        <FormGroup sx={{ pl: 1 }}>
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.sales_orders} onChange={(e) => setResetWipes({ ...resetWipes, sales_orders: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800 }}>🛍️ Sales & Bills Ledger (विक्री व सर्व टेस्ट बिले पुसा)</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.kot_history} onChange={(e) => setResetWipes({ ...resetWipes, kot_history: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800 }}>🍳 Kitchen KOT History (किचन ऑर्डर्सची माहिती)</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.inventory_logs} onChange={(e) => setResetWipes({ ...resetWipes, inventory_logs: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800 }}>📦 Stock & Recipe Logs (इन्व्हेंटरी व्यवहार व साठा 0 करा)</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.accounting_ledger} onChange={(e) => setResetWipes({ ...resetWipes, accounting_ledger: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800 }}>📊 Accounting & Cash Tallies (खर्च नोंदी व कॅश टॅली रिसेट करा)</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.customer_data} onChange={(e) => setResetWipes({ ...resetWipes, customer_data: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800 }}>👥 Customer CRM & Credits (ग्राहक डेटा व उधारी खाती)</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.table_status} onChange={(e) => setResetWipes({ ...resetWipes, table_status: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800 }}>🟢 Reset Table Statuses (सर्व टेबल्स मोकळे करा)</Typography>}
                            />
                            <Divider sx={{ my: 1 }} />
                            <FormControlLabel
                                control={<Checkbox checked={resetWipes.menu_catalog} onChange={(e) => setResetWipes({ ...resetWipes, menu_catalog: e.target.checked })} color="error" />}
                                label={<Typography variant="body2" sx={{ fontWeight: 800, color: '#b91c1c' }}>🍽️ Wipe Menu Items & Categories (मेन्यू पदार्थ व कॅटेगरी पुसा)</Typography>}
                            />
                        </FormGroup>

                        <Divider sx={{ my: 0.5 }} />

                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#b91c1c' }}>
                            पुष्टीकरणासाठी ४-अंकी Developer PIN प्रविष्ट करा:
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            label="Developer PIN"
                            value={resetPinInput}
                            onChange={(e) => setResetPinInput(e.target.value)}
                            inputProps={{ maxLength: 4, style: { letterSpacing: '0.25em', fontWeight: 'bold', textAlign: 'center' } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#fef2f2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                        disabled={resetPinInput.length < 4 || backupLoading}
                        onClick={() => handleDownloadSqlBackup(resetTenant, resetPinInput)}
                        sx={{ fontWeight: 800 }}
                    >
                        {backupLoading ? 'Downloading...' : '📥 Download .sql Backup Only'}
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setResetDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            color="error"
                            disabled={resetLoading || resetPinInput.length < 4 || !resetBranchId}
                            onClick={handleConfirmDataReset}
                            sx={{ fontWeight: 900, borderRadius: 2 }}
                        >
                            {resetLoading ? 'Wiping Data...' : 'Wipe Selected Data (डेटा डिलीट करा)'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Fresh Migration & Seed Confirmation Warning Dialog */}
            <Dialog
                open={freshMigrateOpen}
                onClose={() => setFreshMigrateOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#d97706', bgcolor: '#fffbe6', borderBottom: '1px solid #fef08a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    ⚡ Confirm Fresh Database Migration & Seed (डेटाबेस टेबल्स व सिडिंग तयार करा)
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontWeight: 700 }}>
                        ⚠️ **महत्त्वाची सूचना (WARNING):** या क्रियेमुळे **'{freshMigrateTenant?.name}'** (`{freshMigrateTenant?.subdomain}`) च्या डेटाबेसमधील सर्व जुने टेबल्स डिलीट होऊन **नवीन टेबल्स तयार होतील आणि ओनर अकाउंट, ब्रांचेस व १००+ पदार्थांचे मेनू कार्ड ऑटो-सीड होईल!**
                    </Alert>

                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#1f2937' }}>
                        Target Tenant Database:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                            • Subdomain: <strong>{freshMigrateTenant?.subdomain}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                            • DB Name: <strong>{freshMigrateTenant?.db_name}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                            • Host: <strong>{freshMigrateTenant?.db_host || 'localhost'}</strong>
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#1f2937' }}>
                        Hostinger Database Credentials (जर हॉस्टिंगवर स्वतंत्र युझर/पासवर्ड दिला असेल):
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="MySQL DB User"
                                placeholder="e.g. u905726705_dinexa"
                                value={freshDbUser}
                                onChange={(e) => setFreshDbUser(e.target.value)}
                                helperText="रिकामा सोडल्यास ऑटो-डिटेक्ट करेल"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="password"
                                label="MySQL DB Password"
                                placeholder="DB Password"
                                value={freshDbPass}
                                onChange={(e) => setFreshDbPass(e.target.value)}
                                helperText="रिकामा सोडल्यास ऑनलाईन पास घेईल"
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#d97706', display: 'block', mb: 1 }}>
                        पुष्टीकरणासाठी ४-अंकी Developer PIN प्रविष्ट करा:
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        type="password"
                        label="Developer PIN"
                        value={freshMigratePin}
                        onChange={(e) => setFreshMigratePin(e.target.value)}
                        inputProps={{ maxLength: 4, style: { letterSpacing: '0.25em', fontWeight: 'bold', textAlign: 'center' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#fffbe6' }}>
                    <Button onClick={() => setFreshMigrateOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={freshMigrateLoading || freshMigratePin.length < 4}
                        onClick={handleConfirmFreshMigrate}
                        sx={{ fontWeight: 900, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, borderRadius: 2 }}
                    >
                        {freshMigrateLoading ? 'Migrating & Seeding DB...' : '⚡ Confirm & Run Fresh Migration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Tenant Logs Viewer Dialog */}
            <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, bgcolor: '#0f172a', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc' }}>📄 Tenant Error & Activity Logs</Typography>
                        {logsTenant && (
                            <Chip 
                                label={logsTenant.subdomain} 
                                size="small" 
                                sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 900 }} 
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => fetchLogs(logsTenant?.id)} 
                            sx={{ color: '#38bdf8', borderColor: '#38bdf8', fontWeight: 800, textTransform: 'none' }}
                        >
                            🔄 Refresh Logs
                        </Button>
                        <Button 
                            variant="contained" 
                            color="error" 
                            size="small" 
                            onClick={handleClearLogs} 
                            sx={{ fontWeight: 800, textTransform: 'none' }}
                        >
                            🗑️ Clear Logs
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ bgcolor: '#020617', color: '#f8fafc', p: 2.5 }}>
                    {logsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: '#38bdf8' }} />
                        </Box>
                    ) : (
                        <Box
                            component="pre"
                            sx={{
                                fontFamily: 'Consolas, Monaco, "Fira Code", monospace',
                                fontSize: '0.85rem',
                                lineHeight: 1.6,
                                bgcolor: '#0f172a',
                                color: '#38bdf8',
                                p: 2.5,
                                borderRadius: 2,
                                border: '1px solid #1e293b',
                                maxHeight: 480,
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                m: 0
                            }}
                        >
                            {logsContent}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ bgcolor: '#0f172a', p: 1.5, borderTop: '1px solid #1e293b' }}>
                    <Button onClick={() => setLogsDialogOpen(false)} variant="contained" sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' }, fontWeight: 700 }}>
                        Close Window
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Tenant Database SQL Schema Modal */}
            <Dialog open={sqlModalOpen} onClose={() => setSqlModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 900, bgcolor: '#0f172a', color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <StorageIcon sx={{ color: '#38bdf8' }} />
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#f8fafc' }}>
                            Hostinger / cPanel Tenant Database DDL Script
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopySql}
                            sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, fontWeight: 800, textTransform: 'none' }}
                        >
                            📋 Copy SQL Script
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadSql}
                            sx={{ color: '#38bdf8', borderColor: '#38bdf8', fontWeight: 800, textTransform: 'none' }}
                        >
                            💾 Download .sql
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ bgcolor: '#020617', color: '#f8fafc', p: 3 }}>
                    <Alert severity="info" sx={{ mb: 2, bgcolor: '#0c4a6e', color: '#e0f2fe', border: '1px solid #0284c7', '& .MuiAlert-icon': { color: '#38bdf8' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            💡 <strong>Hostinger वर नवीन क्लायंट डेटाबेस सुरू करण्याच्या ३ सोप्या पायऱ्या:</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, lineHeight: 1.6 }}>
                            1. Hostinger cPanel मध्ये जाऊन नवीन Database तयार करा (उदा. <code>u905726705_dx_clientname</code>).<br />
                            2. phpMyAdmin मध्ये तो डेटाबेस निवडून वरील <strong>"Copy SQL Script"</strong> बटणावर क्लिक करा आणि SQL टॅबमध्ये पेस्ट करून <strong>Go (Run)</strong> करा.<br />
                            3. खालील फॉर्ममध्ये Database Name व Subdomain टाकून <strong>"Register & Setup Tenant Database"</strong> वर क्लिक करा!
                        </Typography>
                    </Alert>

                    <Box
                        component="pre"
                        sx={{
                            fontFamily: 'Consolas, Monaco, "Fira Code", monospace',
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                            bgcolor: '#0f172a',
                            color: '#7dd3fc',
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid #1e293b',
                            maxHeight: 450,
                            overflowY: 'auto',
                            whiteSpace: 'pre',
                            m: 0
                        }}
                    >
                        {TENANT_DDL_SQL_SCRIPT}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ bgcolor: '#0f172a', p: 2, borderTop: '1px solid #1e293b', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        Dinexa POS Enterprise Tenant DDL Schema v2.4 (All 12 tables, indexes & starter records included)
                    </Typography>
                    <Button onClick={() => setSqlModalOpen(false)} variant="contained" sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' }, fontWeight: 700 }}>
                        Close Window
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
